import axios from 'axios';
import API_CONFIG from '../config/api';

// Function to get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Interface for Region
export interface Region {
  _id: string;
  name: string;
  active: boolean;
  order: number;
  streets: string[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Get all active regions for public use (like in NewTestRequest)
 */
export const getActiveRegions = async (): Promise<Region[]> => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('regions/active'),
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در دریافت مناطق');
  } catch (error) {

    throw error;
  }
};

/**
 * Get all regions (for admin use)
 */
export const getAllRegions = async (): Promise<Region[]> => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('regions'),
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در دریافت مناطق');
  } catch (error) {

    throw error;
  }
};

/**
 * Create a new region (admin only)
 */
export const createRegion = async (name: string): Promise<Region> => {
  try {
    const response = await axios.post(
      API_CONFIG.getUrl('regions'),
      { name },
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در ایجاد منطقه');
  } catch (error) {

    throw error;
  }
};

/**
 * Update a region (admin only)
 */
export const updateRegion = async (id: string, data: { name?: string; active?: boolean }): Promise<Region> => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`regions/${id}`),
      data,
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در به‌روزرسانی منطقه');
  } catch (error) {

    throw error;
  }
};

/**
 * Toggle region status (admin only)
 */
export const toggleRegionStatus = async (id: string): Promise<Region> => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`regions/${id}/toggle-status`),
      {},
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در تغییر وضعیت منطقه');
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a region (admin only)
 */
export const deleteRegion = async (id: string): Promise<void> => {
  try {
    const response = await axios.delete(
      API_CONFIG.getUrl(`regions/${id}`),
      getAuthHeaders()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'خطا در حذف منطقه');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Update region order
 */
export const updateRegionOrder = async (id: string, direction: 'up' | 'down'): Promise<Region> => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`regions/${id}/order`),
      { direction },
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در تغییر ترتیب منطقه');
  } catch (error) {

    throw error;
  }
};

/**
 * Add street to region
 */
export const addStreetToRegion = async (regionId: string, street: string): Promise<Region> => {
  try {
    const response = await axios.post(
      API_CONFIG.getUrl(`regions/${regionId}/streets`),
      { street },
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در اضافه کردن خیابان');
  } catch (error) {

    throw error;
  }
};

/**
 * Remove street from region
 */
export const removeStreetFromRegion = async (regionId: string, streetIndex: number): Promise<Region> => {
  try {
    const response = await axios.delete(
      API_CONFIG.getUrl(`regions/${regionId}/streets/${streetIndex}`),
      getAuthHeaders()
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error(response.data.message || 'خطا در حذف خیابان');
  } catch (error) {

    throw error;
  }
};

export default {
  getActiveRegions,
  getAllRegions,
  createRegion,
  updateRegion,
  toggleRegionStatus,
  deleteRegion,
  updateRegionOrder,
  addStreetToRegion,
  removeStreetFromRegion
}; 